import BusinessIcon from "@mui/icons-material/Business";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import RffPhoneField from "../components/finalForm/inputs/RffPhoneField";
import RffTextareaField from "../components/finalForm/inputs/RffTextareaField";
import RffTextField from "../components/finalForm/inputs/RffTextField";
import RffFormChanges from "../components/finalForm/RffFormChanges";
import RffGroup from "../components/finalForm/shared/RffGroup";
import Flx from "../components/layout/Flx";
import TitledHeader from "../components/layout/TitledHeader";
import { useUsersHook } from "../hooks/useUsersHook";
import { userActionSet } from "../redux/actions/userActions";

import {
  CloseOutlined,
  EditOutlined,
  InsertLink,
  VisibilityOutlined,
} from "@mui/icons-material";
import { formatPhone } from "../_src_shared/utils/functions/formatPhone";
import joinArrayOfStrings from "../_src_shared/utils/functions/joinArrayOfStrings";
import RffFieldsRenderer from "../components/finalForm/shared/RffFieldsRenderer";
import BlockNoteEditor from "../components/html/BlockNoteEditor";
import Screen from "../components/layout/Screen";
import BasicModal from "../components/modals/BasicModal";
import InfoGrid from "../components/ui/data/InfoGrid";
import { getFirstLetterOfString } from "../utils/strings/getFirstLetterOfString";
import HtmlEditor from "../components/html/HtmlEditor";
import { getDefaultEmailFooterHtml } from "../utils/email/getDefaultEmailFooterHtml";
import TabSwitcher from "../components/navigation/TabSwitcher";

// ─── Section nav config ───────────────────────────────────────────────
const NAV_ITEMS = [
  {
    key: "personal",
    label: "Personal Info",
    icon: <PersonOutlineIcon fontSize="small" />,
  },
  { key: "company", label: "Company", icon: <BusinessIcon fontSize="small" /> },
  // {
  //   key: "signature",
  //   label: "Email Signature",
  //   icon: <EmailOutlinedIcon fontSize="small" />,
  // },
];

// ─── Section card wrapper ─────────────────────────────────────────────
const SectionCard = React.forwardRef(({ title, endContent, children }, ref) => (
  <Paper
    ref={ref}
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 3,
      p: 3,
      mb: 2.5,
    }}
  >
    <Flx fw jb ac sx={{ mb: 2.5 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, letterSpacing: "-0.01em", fontSize: "17px" }}
      >
        {title}
      </Typography>
      {endContent}
    </Flx>
    {children}
  </Paper>
));

// ─── Main Component ───────────────────────────────────────────────────
const ProfileScreen = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState("personal");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const sectionRefs = useRef({});

  // Signature modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const { loading: usersLoading, updateUserById } = useUsersHook();

  // Scroll-to-section
  const handleNavClick = useCallback((key) => {
    setActiveSection(key);
    sectionRefs.current[key]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // Ref setter for sections
  const setSectionRef = useCallback(
    (key) => (el) => {
      sectionRefs.current[key] = el;
    },
    [],
  );

  const personalInfoItems = useMemo(() => {
    return [
      {
        label: "First Name",
        name: "firstName",
        value: user?.firstName,
        disabled: true,
        size: 6,
      },
      {
        label: "Last Name",
        name: "lastName",
        disabled: true,
        size: 6,
        value: user?.lastName,
      },
      {
        label: "Email",
        name: "emailAddress",
        disabled: true,
        value: user?.emailAddress,
      },
      {
        label: "Phone",
        name: "phone",
        type: "phone",
        value: formatPhone(user?.phone),
      },
    ];
  }, [user]);

  const companyInfoItems = useMemo(() => {
    return [
      { label: "Company", name: "company", value: user?.company },
      { label: "Role", disabled: true, value: joinArrayOfStrings(user?.roles) },
    ];
  }, [user]);

  // ── Signature save handler ──────────────────────────────────────────
  const handleSaveSignature = useCallback(
    ({ emailSignature, signatureFields }) => {
      updateUserById({
        userId: user._id,
        data: {
          emailSignature,
          ...(signatureFields ? { signatureFields } : {}),
        },
        onSuccessFn: (updatedUser) => {
          dispatch(userActionSet(updatedUser));
          setShowSignatureModal(false);
        },
        onErrorFn: (err) => {
          console.error("Failed to save signature:", err);
        },
      });
    },
    [user?._id, updateUserById, dispatch],
  );

  return (
    <Screen>
      <TitledHeader title="Profile Settings" />
      <Box sx={{ flexGrow: 1, overflowY: "auto", bgcolor: "grey.50" }}>
        <Box
          sx={{
            display: "flex",
            maxWidth: 960,
            mx: "auto",
            px: 3,
            py: 3.5,
            gap: 3.5,
          }}
        >
          {/* ── Left Nav ───────────────────────────────────────────── */}
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              position: "sticky",
              top: 40,
              alignSelf: "flex-start",
            }}
          >
            {/* Mini profile */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <Box sx={{ position: "relative" }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "primary.main",
                    fontWeight: 700,
                    fontSize: 16,
                  }}
                >
                  {getFirstLetterOfString(user?.firstName)}
                  {getFirstLetterOfString(user?.lastName)}
                </Avatar>
              </Box>
              <Box sx={{ overflow: "hidden" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, noWrap: true }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                  noWrap
                >
                  {joinArrayOfStrings(user?.roles)}
                </Typography>
              </Box>
            </Box>

            {/* Nav list */}
            <List
              disablePadding
              sx={{
                "& .MuiListItemButton-root": { borderRadius: 2, mb: 0.25 },
              }}
            >
              {NAV_ITEMS.map((item) => (
                <ListItemButton
                  key={item.key}
                  selected={activeSection === item.key}
                  onClick={() => handleNavClick(item.key)}
                  sx={{
                    py: 1,
                    px: 1.5,
                    "&.Mui-selected": {
                      bgcolor: "primary.50",
                      color: "primary.main",
                      "& .MuiListItemIcon-root": { color: "primary.main" },
                      "&:hover": { bgcolor: "primary.50" },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* ── Right Content ──────────────────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Personal Information */}
            <SectionCard
              ref={setSectionRef("personal")}
              title="Personal Information"
              endContent={
                <UpdateProfileInfoButton fields={personalInfoItems} />
              }
            >
              <SectionDisplayFields fields={personalInfoItems} />
            </SectionCard>

            {/* Company & Office */}
            <SectionCard
              ref={setSectionRef("company")}
              title="Company & Office"
              endContent={<UpdateProfileInfoButton fields={companyInfoItems} />}
            >
              <SectionDisplayFields fields={companyInfoItems} />
            </SectionCard>

            {/* ── Email Signature ───────────────────────────────────── */}
            {/* <SectionCard
              ref={setSectionRef("signature")}
              title="Email Signature"
              endContent={
                <Tooltip title="Edit Signature" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShowSignatureModal(true)}
                  >
                    <EditOutlined className="thin" sx={{ fontSize: "18px" }} />
                  </IconButton>
                </Tooltip>
              }
            >
              <SignaturePreview html={user?.emailSignature} />
            </SectionCard> */}
          </Box>
        </Box>
      </Box>

      {/* ── Signature Block Modal ────────────────────────────────── */}
      {showSignatureModal && (
        <SignatureBlockModal
          show={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          user={user}
          onSave={handleSaveSignature}
          loading={usersLoading}
        />
      )}
    </Screen>
  );
};

// ─── Signature Preview (read-only HTML render) ────────────────────────
const SignaturePreview = ({ html }) => {
  if (!html) {
    return (
      <Box
        sx={{
          py: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <EmailOutlinedIcon sx={{ fontSize: 32, color: "text.disabled" }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No email signature configured yet.
        </Typography>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          Click the edit button to create your signature block.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        bgcolor: "#fff",
        "& a": { color: "#1976d2" },
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Paper>
  );
};

// ─── Shared sub-components (unchanged) ────────────────────────────────

const SectionDisplayFields = ({ fields }) => {
  return (
    <InfoGrid
      gap={2}
      row
      sx={{ pt: 2 }}
      labelFontSize="12px"
      labelUppercase={false}
      rowDivider
      width="260px"
      mb={1}
      items={fields}
    />
  );
};

const UpdateProfileInfoButton = ({ fields }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Tooltip title="Edit" arrow>
        <IconButton size="small" onClick={() => setShow(true)}>
          <EditOutlined className="thin" sx={{ fontSize: "18px" }} />
        </IconButton>
      </Tooltip>
      {show && (
        <BasicModal
          maxWidth="sm"
          show={show}
          onClose={() => setShow(false)}
          title={"Update Info"}
        >
          <UpdateProfileForm
            fields={fields}
            onSuccessFn={() => setShow(false)}
          />
        </BasicModal>
      )}
    </>
  );
};

const UpdateProfileForm = ({ fields, onSuccessFn }) => {
  const user = useSelector((state) => state.user);
  const { loading, updateUserById } = useUsersHook();
  const dispatch = useDispatch();

  const handleOnUpdate = useCallback((changedData) => {
    updateUserById({
      userId: user._id,
      data: {
        ...changedData,
      },
      onSuccessFn: (updatedUser) => {
        dispatch(userActionSet(updatedUser));
        onSuccessFn();
      },
      onErrorFn: (err) => {
        console.error("Failed to sync preferences to profile:", err);
      },
    });
  }, []);

  const init = useMemo(() => ({ ...user }), [user]);

  return (
    <RffFormChanges
      submitText={"Update"}
      initialValues={init}
      onSubmit={handleOnUpdate}
      loading={loading}
    >
      <RffGroup>
        <RffFieldsRenderer fields={fields} />
      </RffGroup>
    </RffFormChanges>
  );
};

// ─── Generate signature HTML from field values ────────────────────────
const buildSignatureHtml = ({
  fullName,
  title,
  company,
  phone,
  email,
  tagline,
  links,
}) => {
  const rows = [];

  if (fullName) {
    rows.push(
      `<strong style="font-size:16px;color:#1a1a1a;">${fullName}</strong>`,
    );
  }
  if (title) {
    rows.push(`<span style="font-size:13px;color:#666;">${title}</span>`);
  }
  if (phone) {
    rows.push(
      `<span style="font-size:13px;color:#1a1a1a;">📞&nbsp; ${phone}</span>`,
    );
  }
  if (email) {
    rows.push(
      `<a href="mailto:${email}" style="font-size:13px;color:#1976d2;text-decoration:none;">${email}</a>`,
    );
  }
  if (tagline) {
    rows.push(`<span style="font-size:13px;color:#1a1a1a;">${tagline}</span>`);
  }

  // Link buttons
  const linkButtons = (links || [])
    .filter((l) => l.displayText && l.url)
    .map(
      (l) =>
        `<a href="${l.url}" style="display:inline-block;padding:8px 18px;background:#1976d2;color:#fff;text-decoration:none;border-radius:4px;font-size:13px;font-weight:600;">${l.displayText}</a>`,
    );

  if (linkButtons.length > 0) {
    rows.push(linkButtons.join("&nbsp; "));
  }

  return `<div style="font-family:Arial,sans-serif;line-height:1.8;">${rows.join("<br/>")}</div>`;
};

// ─── Parse existing signature HTML back into field values ─────────────
const parseSignatureFields = (html, user) => {
  // Default from user object
  const defaults = {
    fullName: [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "",
    title: "",
    company: user?.company || "",
    phone: user?.phone || "",
    email: user?.emailAddress || "",
    tagline: "",
    links: [],
    enableTrackingPixel: false,
  };

  if (!html) return defaults;

  // Try to extract values from existing HTML
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Extract name from <strong>
    const strong = doc.querySelector("strong");
    if (strong) defaults.fullName = strong.textContent.trim();

    // Extract links (buttons)
    const anchors = doc.querySelectorAll('a[style*="background"]');
    if (anchors.length > 0) {
      defaults.links = Array.from(anchors).map((a) => ({
        displayText: a.textContent.trim(),
        url: a.getAttribute("href") || "",
        useJwt: false,
      }));
    }
  } catch {
    // If parsing fails, just use defaults
  }

  return defaults;
};

// ─── Signature field input ────────────────────────────────────────────
const SigField = ({
  label,
  value,
  onChange,
  placeholder,
  multiline,
  ...rest
}) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    multiline={multiline}
    minRows={multiline ? 2 : undefined}
    sx={{ mb: 1.5 }}
    {...rest}
  />
);

// ─── Link item chip ───────────────────────────────────────────────────
const LinkItem = ({ link, onRemove }) => (
  <Paper
    variant="outlined"
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1,
      mb: 1,
      borderRadius: 1.5,
      bgcolor: "action.hover",
    }}
  >
    <Box sx={{ overflow: "hidden" }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "primary.main" }}
        noWrap
      >
        {link.displayText}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
        {link.url}
      </Typography>
    </Box>
    <IconButton size="small" onClick={onRemove}>
      <CloseOutlined sx={{ fontSize: 16 }} />
    </IconButton>
  </Paper>
);
const EDIT_MODE_TYPES = {
  HTML: "html",
  FIELDS: "fields",
};
// ─── Main Modal Component ─────────────────────────────────────────────
const SignatureBlockModal = ({ show, onClose, user, onSave, loading }) => {
  const [fields, setFields] = useState(() =>
    parseSignatureFields(user?.emailSignature, user),
  );
  const [editMode, setEditMode] = useState(EDIT_MODE_TYPES.FIELDS);
  const [rawHtml, setRawHtml] = useState("");

  // New link form state
  const [newLinkText, setNewLinkText] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkJwt, setNewLinkJwt] = useState(false);

  // Re-initialize if user changes
  useEffect(() => {
    if (show) {
      const parsed = parseSignatureFields(user?.emailSignature, user);
      setFields(parsed);
      setEditMode(EDIT_MODE_TYPES.FIELDS);
    }
  }, [show, user]);
  // Update a single field
  const updateField = useCallback((key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleEditModeChange = useCallback(
    (newMode) => {
      if (
        newMode === EDIT_MODE_TYPES.HTML &&
        editMode !== EDIT_MODE_TYPES.HTML
      ) {
        setRawHtml(buildSignatureHtml(fields));
      }
      setEditMode(newMode);
    },
    [editMode, fields],
  );

  const previewHtml = useMemo(() => {
    if (editMode === EDIT_MODE_TYPES.HTML) return rawHtml;
    return buildSignatureHtml(fields);
  }, [fields, editMode, rawHtml]);
  // Toggle raw HTML mode

  // Add link
  const handleAddLink = useCallback(() => {
    if (!newLinkText.trim() || !newLinkUrl.trim()) return;
    setFields((prev) => ({
      ...prev,
      links: [
        ...(prev.links || []),
        {
          displayText: newLinkText.trim(),
          url: newLinkUrl.trim(),
          useJwt: newLinkJwt,
        },
      ],
    }));
    setNewLinkText("");
    setNewLinkUrl("");
    setNewLinkJwt(false);
  }, [newLinkText, newLinkUrl, newLinkJwt]);

  // Remove link
  const handleRemoveLink = useCallback((index) => {
    setFields((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  }, []);

  // Save
  const handleSave = useCallback(() => {
    const html =
      editMode === EDIT_MODE_TYPES.HTML ? rawHtml : buildSignatureHtml(fields);
    onSave({
      emailSignature: html,
      signatureFields: editMode === EDIT_MODE_TYPES.HTML ? null : fields,
    });
  }, [fields, editMode, rawHtml, onSave]);
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title={
        <Flx ac g={1}>
          <EditOutlined sx={{ fontSize: 20 }} />
          <span>Signature Block — {userName}</span>
        </Flx>
      }
      maxWidth="md"
      suppressFooter
    >
      <Box sx={{ px: 0.5 }}>
        {/* ── Top: Fields + Preview ───────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          {/* Left: Signature Fields */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TabSwitcher
              value={editMode}
              onChange={handleEditModeChange}
              sx={{ mb: 1 }}
              tabs={[
                { label: "Signature Fields", value: EDIT_MODE_TYPES.FIELDS },
                { label: "Edit As HTML", value: EDIT_MODE_TYPES.HTML },
              ]}
            />
            {editMode === EDIT_MODE_TYPES.HTML ? (
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={14}
                size="small"
                value={rawHtml}
                onChange={(e) => setRawHtml(e.target.value)}
                placeholder="Paste your custom HTML signature..."
                sx={{
                  fontFamily: "monospace",
                  "& textarea": { fontFamily: "monospace", fontSize: 12 },
                }}
              />
            ) : (
              <>
                <SigField
                  placeholder="Full Name"
                  value={fields.fullName}
                  onChange={(v) => updateField("fullName", v)}
                />
                <SigField
                  placeholder="Title / Role"
                  value={fields.title}
                  onChange={(v) => updateField("title", v)}
                />
                <SigField
                  placeholder="Company Name"
                  value={fields.company}
                  onChange={(v) => updateField("company", v)}
                />
                <Flx g={1.5}>
                  <SigField
                    placeholder="Phone"
                    value={fields.phone}
                    onChange={(v) => updateField("phone", v)}
                  />
                  <SigField
                    placeholder="Email"
                    value={fields.email}
                    onChange={(v) => updateField("email", v)}
                  />
                </Flx>
                <SigField
                  placeholder="Tagline or message"
                  value={fields.tagline}
                  onChange={(v) => updateField("tagline", v)}
                  multiline
                />
              </>
            )}
          </Box>

          {/* Right: Live Preview */}
          <Box sx={{ width: 340, flexShrink: 0 }}>
            <Flx ac g={0.5} sx={{ mb: 1.5 }}>
              <VisibilityOutlined
                sx={{ fontSize: 14, color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Live Preview
              </Typography>
            </Flx>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                minHeight: 160,
                borderRadius: 2,
                bgcolor: "#fff",
                "& a": { color: "#1976d2" },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </Paper>
          </Box>
        </Box>

        {/* ── Tracking Pixel ──────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{ px: 2, py: 1, mb: 2.5, borderRadius: 2 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={fields.enableTrackingPixel || false}
                onChange={(e) =>
                  updateField("enableTrackingPixel", e.target.checked)
                }
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Enable Tracking Pixel
              </Typography>
            }
          />
        </Paper>

        {/* ── Insert Links ────────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{ px: 2.5, py: 2, mb: 3, borderRadius: 2 }}
        >
          <Flx ac g={1} sx={{ mb: 0.5 }}>
            <InsertLink sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Insert Links
            </Typography>
          </Flx>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 2 }}
          >
            Add clickable links to your signature. The display text is what
            recipients see (e.g. &quot;Fill out loan application&quot;). Links
            appear as styled buttons in the signature.
          </Typography>

          {/* Existing links */}
          {(fields.links || []).map((link, i) => (
            <LinkItem
              key={i}
              link={link}
              onRemove={() => handleRemoveLink(i)}
            />
          ))}

          {/* New link inputs */}
          <TextField
            fullWidth
            size="small"
            placeholder="Display text (e.g. Fill out loan application)"
            value={newLinkText}
            onChange={(e) => setNewLinkText(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Flx ac g={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Link URL (https://...)"
              value={newLinkUrl}
              onChange={(e) => setNewLinkUrl(e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={newLinkJwt}
                  onChange={(e) => setNewLinkJwt(e.target.checked)}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  JWT
                </Typography>
              }
              sx={{ ml: 0, mr: 0 }}
            />
            <Button
              size="small"
              variant="text"
              onClick={handleAddLink}
              disabled={!newLinkText.trim() || !newLinkUrl.trim()}
              sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
              startIcon={<span>+</span>}
            >
              Insert
            </Button>
          </Flx>
        </Paper>

        {/* ── Footer Actions ──────────────────────────────────────── */}
        <Flx jfe ac g={1.5} sx={{ pb: 1 }}>
          <Button variant="text" onClick={onClose} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
            sx={{ fontWeight: 600, px: 3 }}
          >
            {loading ? "Saving..." : "Save Signature"}
          </Button>
        </Flx>
      </Box>
    </BasicModal>
  );
};

export default ProfileScreen;
